import { EditActions } from '@/components';
import {
	EDITOR_IMAGE_ACCEPT,
	FILE_SIZE_2MB_BYTE,
	PageTypeEnum,
} from '@/constant';
import { useEditableDetail, useMergingState } from '@/hooks';
import { cn, compressImage, isFileTypeInvalid, NOOP, showToast } from '@/utils';
import {
	forwardRef,
	lazy,
	memo,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import type TReactQuill from 'react-quill';
import { Range, ReactQuillProps, UnprivilegedEditor } from 'react-quill/lib';

import 'react-quill/dist/quill.snow.css';
import './styles.scss';
import Quill from 'quill';

const Image = Quill.import('formats/image');

class CustomImage extends Image {
	static blotName = 'image';
	static tagName = 'img';

	static create(value: any) {
		const node = super.create(value.src || value);

		if (value['url']) {
			node.setAttribute('src', value['url']);
		}
		if (value['alt']) {
			node.setAttribute('alt', value['alt']);
		}
		if (value['data-size']) {
			node.setAttribute('data-size', value['data-size']);
		}
		if (value['data-id']) {
			node.setAttribute('data-id', value['data-id']);
		}
		if (value['data-name']) {
			node.setAttribute('data-name', value['data-name']);
		}
		if (value['data-file-id']) {
			node.setAttribute('data-file-id', value['data-file-id']);
		}

		return node;
	}

	static value(node: HTMLImageElement) {
		return {
			url: node.getAttribute('src'),
			alt: node.getAttribute('alt'),
			'data-size': node.getAttribute('data-size'),
			'data-id': node.getAttribute('data-id'),
			'data-name': node.getAttribute('data-name'),
			'data-file-id': node.getAttribute('data-file-id'),
		};
	}
}

Quill.register(CustomImage, true);

const ReactQuill = lazy(() => import('react-quill'));

const formats = [
	'bold',
	'italic',
	'underline',
	'strike',
	'blockquote',
	'code-block',
	'header',
	'list',
	'bullet',
	'script',
	'indent',
	'direction',
	'size',
	'color',
	'background',
	'font',
	'align',
	'link',
	'image',
];

export interface ITextEditorProps extends ReactQuillProps {
	formik?: any;
	touched?: boolean;
	errorCode?: string | undefined;
	hasServerError?: boolean;
	maxContentImageSize?: number;
	name?: string;
	isDetail?: boolean;
	disabled?: boolean;
	initialImageIds?: string[];
	initialContent?: string;
	refTableName?: PageTypeEnum;
	refTableId?: string;
	documentType?: string;
}

export type TTextEditorRef = {
	isUploading?: boolean;
	base64UrlToFileMap?: Map<string, File>;
	quill?: TReactQuill;
};

const TextEditor = (
	{
		formik,
		className,
		placeholder = 'Write a description or drag your files here...',
		value,
		touched,
		errorCode,
		hasServerError,
		maxContentImageSize = FILE_SIZE_2MB_BYTE,
		isDetail = false,
		disabled = false,
		initialImageIds = [],
		initialContent,
		refTableName,
		refTableId,
		documentType,
		...rest
	}: ITextEditorProps,
	ref: React.Ref<TTextEditorRef>
) => {
	const { t } = useTranslation('message');

	const [state, setState] = useMergingState({
		editable: false,
		imageLoading: false,
		base64UrlToFileMap: new Map() as Map<string, File> | undefined,
	});

	const textEditorRef = useRef<TReactQuill>(null);

	useImperativeHandle(ref, () => ({
		isUploading: state.imageLoading,
		base64UrlToFileMap: state.base64UrlToFileMap,
		quill: textEditorRef.current || undefined,
	}));

	const { editable, handleLabelClick, onCancel, onSave } = useEditableDetail({
		fieldName: rest.name as string,
	});

	const handleImageUpload = useCallback(() => {
		const input = document.createElement('input');
		input.setAttribute('type', 'file');
		input.setAttribute('accept', EDITOR_IMAGE_ACCEPT);
		input.click();

		document.body.appendChild(input);

		input.onchange = async () => {
			const file = (input.files as FileList)[0];
			document.body.removeChild(input);

			if (file) {
				if (isFileTypeInvalid(EDITOR_IMAGE_ACCEPT, file.name)) {
					showToast(t('COMMON_18'), { type: 'ERROR' });
					return;
				}

				setState({
					imageLoading: true,
				});

				try {
					const compressedFile = (await compressImage(file)) || file;

					const reader = new FileReader();

					const quill = textEditorRef.current?.getEditor();
					if (!quill) return;

					reader.onload = () => {
						const imageUrl = reader.result;
						if (!imageUrl || typeof imageUrl !== 'string') {
							console.warn('Image URL is invalid:', imageUrl);
							return;
						}

						const quill = textEditorRef.current?.getEditor();
						if (!quill) return;

						// Ensure editor is focused
						quill.focus();
						const range = quill.getSelection(true);

						if (range) {
							quill.insertEmbed(range.index, 'image', {
								src: imageUrl,
								'data-size': compressedFile.size,
							});
							quill.setSelection({ index: range.index + 1, length: 0 });
						} else {
							console.warn('No selection range');
						}

						setState((prev) => {
							const newMap = new Map(prev.base64UrlToFileMap);
							newMap.set(imageUrl, compressedFile);
							return {
								...prev,
								base64UrlToFileMap: newMap,
							};
						});
					};

					reader.onerror = (err) => {
						console.error('reader error', err);
					};

					if (compressedFile) reader.readAsDataURL(compressedFile);
				} catch (error) {
					console.error('Image compression failed:', error);
				} finally {
					setState({ imageLoading: false });
				}
			}
		};
	}, []);

	const modules = useMemo(
		() => ({
			toolbar: {
				container: [
					['bold', 'italic', 'underline', 'strike'], // toggled buttons
					['blockquote', 'code-block'],
					[{ header: 1 }, { header: 2 }], // custom button values
					[{ list: 'ordered' }, { list: 'bullet' }],
					[{ script: 'sub' }, { script: 'super' }], // superscript/subscript
					[{ indent: '-1' }, { indent: '+1' }], // outdent/indent
					[{ direction: 'rtl' }], // text direction
					[{ size: ['small', false, 'large', 'huge'] }], // custom dropdown
					[{ header: [1, 2, 3, 4, 5, 6, false] }],
					[{ color: [] }, { background: [] }], // dropdown with defaults from theme
					[{ font: [] }],
					[{ align: [] }],
					['clean'], // remove formatting button
					['link', 'image'], // link and image, video
				],
				handlers: {
					image: handleImageUpload,
				},
			},
		}),
		[handleImageUpload]
	);

	useEffect(() => {
		setState({
			editable,
		});
		if (editable && isDetail) {
			textEditorRef?.current?.focus();
		}
	}, [editable]);

	useEffect(() => {
		const quill = textEditorRef.current?.getEditor?.();
		if (!quill) return;

		const handlePaste = (e: ClipboardEvent) => {
			const items = e.clipboardData?.items;
			if (!items) return;

			for (let i = 0; i < items.length; i++) {
				if (items[i].type.indexOf('image') !== -1) {
					e.preventDefault();
					console.warn('Pasting images is disabled');
					return;
				}
			}
		};

		const editorElement = quill.root;
		editorElement.addEventListener('paste', handlePaste);

		return () => {
			editorElement.removeEventListener('paste', handlePaste);
		};
	}, []);

	const handleSelectionChange = useCallback(
		(range: Range, source: any, editor: UnprivilegedEditor) => {
			if (state.editable) return;
			if (range && source === 'user') {
				handleLabelClick();
				setState({
					editable: true,
				});
			}
		},
		[state.editable, handleLabelClick]
	);

	return (
		<>
			<div className="relative w-full">
				{state.imageLoading && (
					<div className="absolute z-10 h-full w-full bg-white opacity-50" />
				)}
				<ReactQuill
					theme="snow"
					value={value}
					onChangeSelection={handleSelectionChange}
					modules={modules}
					formats={formats}
					className={cn(
						'h-[660px] bg-white [&>.ql-toolbar]:min-h-[45px] [&>.ql-container]:max-h-[615.5px]',
						isDetail &&
							!state.editable &&
							'[&>.ql-toolbar]:hidden [&>.ql-container]:max-h-[660px] [&>.ql-container]:border-0 cursor-text hover:border hover:border-gray1',
						disabled &&
							'[&>.ql-toolbar]:hidden [&>.ql-container]:max-h-full [&>.ql-container.ql-snow]:border-none',
						className,
						((touched && errorCode) || hasServerError) && 'error'
					)}
					placeholder={placeholder}
					onBlur={isDetail ? NOOP : rest.onBlur}
					{...rest}
					ref={textEditorRef}
					preserveWhitespace={true}
					readOnly={disabled || (isDetail && !state.editable)}
				/>
				{!disabled && isDetail && state.editable && (
					<EditActions
						onCancel={onCancel}
						onSave={onSave}
						className="absolute bottom-0 right-0 border border-gray1 px-2 py-1.5 gap-2.5 bg-white"
					/>
				)}
			</div>
		</>
	);
};

export default memo(forwardRef<TTextEditorRef, ITextEditorProps>(TextEditor));
